import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Upload, CheckCircle2, AlertTriangle, Loader2, Receipt, Plus, X, Send } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ComptaScanMobile = () => {
  const { sessionId } = useParams();
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/compta/scan-session/${sessionId}`)
      .then(r => {
        if (r.data.status === 'processed') {
          setStatus('already');
        } else {
          setUploadedCount(r.data.file_count || 0);
          setStatus('ready');
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg('Lien invalide ou expire. Veuillez regenerer le QR code.');
      });
  }, [sessionId]);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/compta/scan-session/${sessionId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedCount(res.data.file_count || uploadedCount + 1);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setUploading(false);
    // Reset file input
    e.target.value = '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4" data-testid="compta-scan-mobile">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-3">
            <Receipt className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Vortekio Comptabilite</h1>
          <p className="text-sm text-slate-500">Scanner une facture fournisseur</p>
        </div>

        {status === 'loading' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Verification...</p>
          </div>
        )}

        {status === 'ready' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            {/* Counter */}
            {uploadedCount > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="upload-counter">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-bold text-emerald-700">
                    {uploadedCount} page{uploadedCount > 1 ? 's' : ''} envoyee{uploadedCount > 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs text-emerald-600 mt-1">Vous pouvez ajouter d'autres pages ou fermer</p>
              </div>
            )}

            {/* Instructions */}
            {uploadedCount === 0 && (
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <Camera className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-800">Prenez une photo de la facture</p>
                <p className="text-xs text-emerald-600 mt-1">Plusieurs pages ? Prenez chaque page une par une</p>
              </div>
            )}

            {uploading ? (
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-600">Envoi en cours...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input id="mobile-cam" type="file" accept="image/*" capture="environment"
                    onChange={handleCapture} style={{ display: 'none' }} />
                  <label htmlFor="mobile-cam" data-testid="mobile-camera-btn"
                    className="flex flex-col items-center gap-2 w-full py-5 rounded-xl bg-emerald-600 active:bg-emerald-800 text-white font-bold text-base cursor-pointer touch-manipulation text-center">
                    {uploadedCount > 0 ? <Plus className="w-7 h-7" /> : <Camera className="w-7 h-7" />}
                    {uploadedCount > 0 ? 'Page suivante' : 'Photo'}
                  </label>
                </div>
                <div>
                  <input id="mobile-file" type="file" accept="image/*,.pdf,application/pdf"
                    onChange={handleCapture} style={{ display: 'none' }} />
                  <label htmlFor="mobile-file" data-testid="mobile-file-btn"
                    className="flex flex-col items-center gap-2 w-full py-5 rounded-xl bg-slate-200 active:bg-slate-300 text-slate-700 font-bold text-base cursor-pointer touch-manipulation text-center">
                    <Upload className="w-7 h-7" />
                    Fichier
                  </label>
                </div>
              </div>
            )}

            {uploadedCount > 0 && !uploading && (
              <p className="text-xs text-center text-slate-400 mt-2">
                L'analyse se lancera automatiquement sur votre PC
              </p>
            )}
          </div>
        )}

        {status === 'already' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-8 h-8 text-amber-600" />
            </div>
            <p className="text-lg font-bold text-amber-700">Deja traite</p>
            <p className="text-sm text-slate-500 mt-2">Cette session a deja ete analysee.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-lg font-bold text-red-700">Erreur</p>
            <p className="text-sm text-slate-500 mt-2">{errorMsg}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComptaScanMobile;
